import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { Breadcrumbs } from "@/components/feature/PageShell";
import { siteMeta } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

const principles = [
  {
    icon: "ri-quill-pen-line",
    title: "编辑视角优先于榜单",
    text: "我们不按热度堆砌排名，而是为每一部条目写一段可被验证的观看提示：它讨论了什么困境、镜头放在哪里、为什么值得用两小时。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不虚构真实信息",
    text: "站内所有剧名、人物、评分与播出平台均为虚构演示数据，不对应任何真实作品。这是我们对内容的底线，也是对读者的尊重。",
  },
  {
    icon: "ri-book-read-line",
    title: "为深度阅读而生",
    text: "版面以长文阅读为基准设计：行距宽松、字号克制、信息分层清晰，适合在电脑与手机上一口气读完一个专题。",
  },
];

const notices = [
  "本站为内容演示站点，不提供任何影视作品的在线播放、下载、云盘或磁力链接服务。",
  "站内所有条目资料、评分、导演与演员姓名均为虚构，如有雷同纯属巧合，不构成对任何真实作品或个人的评价。",
  "收藏与浏览记录功能仅为本地演示，数据保存在你自己的浏览器 localStorage 中，不会上传服务器，清除浏览器数据后将无法恢复。",
  "站内图片均为演示用生成素材，不含任何真实剧照或受版权保护的影视画面。",
  "如你是内容创作者并希望合作，欢迎通过下方邮箱联系编辑部。",
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 日本伦理剧",
    description:
      "日本伦理剧是一个以杂志编辑视角整理日本伦理题材影视作品的内容站点，提供剧集资料、主题专题与观看指引。本站为虚构演示内容，不提供播放与下载服务。",
    keywords: "日本伦理剧,关于我们,日本伦理题材影视,专题策划",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="container-page py-10 md:py-16">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <p className="mt-6 rule-label text-accent-700">ABOUT THE EDITORIAL</p>
            <h1 className="mt-4 max-w-3xl font-heading text-[28px] font-semibold leading-tight text-foreground-950 md:text-[44px]">
              关于「日本伦理剧」
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-loose text-foreground-600 md:text-[15px]">
              一个专注日本伦理题材影视内容的独立编辑档案。我们相信，伦理剧的价值不在于给出答案，而在于把问题摆放整齐，让观众自己走进去。
            </p>
          </div>
        </section>

        <section className="container-page py-12 md:py-16">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">网站定位</h2>
              <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-700">
                <p>
                  {siteMeta.description}
                </p>
                <p>
                  我们把「伦理」理解为一个可以被拆解的题目，而不是一个标签。家庭里的养育与继承、职场里的举报与沉默、年代背景下的对错漂移——这些命题在不同的剧集里反复出现，却因为叙事视角不同而给出完全不同的答案。编辑部的工作，就是把这些差异整理出来。
                </p>
                <p>
                  网站结构分为三类入口：按题材进入分类页做横向比较；按专题进入策划页做纵深阅读；也可以直接搜索剧名、导演、演员或标签，快速定位一个具体条目。
                </p>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {[
                  { value: "6", label: "题材分类" },
                  { value: "12", label: "收录条目" },
                  { value: "5", label: "策划专题" },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="font-heading text-2xl font-semibold text-primary-900">{item.value}</p>
                    <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>

              <h2 className="mt-12 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">编辑部原则</h2>
              <div className="mt-4 space-y-3">
                {principles.map((item) => (
                  <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="mt-12 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">版权声明与使用须知</h2>
              <ul className="mt-4 space-y-3">
                {notices.map((notice, index) => (
                  <li key={index} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                    <span className="font-label text-[11px] text-accent-700">{String(index + 1).padStart(2, "0")}</span>
                    <span>{notice}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 space-y-4">
                <div className="rounded-lg border border-background-200 bg-secondary-50 p-6">
                  <p className="rule-label text-secondary-800">联系方式</p>
                  <p className="mt-3 font-heading text-lg font-semibold text-foreground-950">编辑部邮箱</p>
                  <p className="mt-2 font-label text-sm text-accent-800 break-all">{siteMeta.email}</p>
                  <p className="mt-4 text-xs leading-relaxed text-foreground-700">
                    内容勘误、专题投稿与合作咨询请通过邮箱联系。本站当前为纯展示站点，未开放在线表单，邮件回复通常在三个工作日内。
                  </p>
                  <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
                    提示：以上邮箱为演示地址，不对应真实收件箱。
                  </p>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <p className="rule-label text-foreground-500">从哪里开始读</p>
                  <ul className="mt-3 divide-y divide-background-200">
                    <li>
                      <Link
                        to="/special/family-web"
                        className="flex items-center justify-between py-3 text-sm text-foreground-800 hover:text-accent-700"
                      >
                        家庭伦理专题 <i className="ri-arrow-right-s-line text-foreground-400" />
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/special/quiet-voices"
                        className="flex items-center justify-between py-3 text-sm text-foreground-800 hover:text-accent-700"
                      >
                        沉默的重量：职场与良知 <i className="ri-arrow-right-s-line text-foreground-400" />
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/category/era-showa"
                        className="flex items-center justify-between py-3 text-sm text-foreground-800 hover:text-accent-700"
                      >
                        年代昭和分类 <i className="ri-arrow-right-s-line text-foreground-400" />
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <p className="rule-label text-foreground-500">题材入口</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.key}
                        to={`/category/${cat.key}`}
                        className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}